import { useState } from 'react';

import { useCapability } from '../../../hooks/use-capability';
import { FeatureLocked } from '../../shared/FeatureLocked';

import { useMcpHandshakeListener } from './hooks/use-mcp-handshake-listener';
import { AiTab } from './tabs/AiTab';
import { CalendarTab } from './tabs/CalendarTab';
import { ChannelsTab } from './tabs/ChannelsTab';
import { ErpsCrmsTab } from './tabs/ErpsCrmsTab';

type TabId = 'erps-crms' | 'channels' | 'ai' | 'calendar';

/**
 * Sprint A4 S1 PR 3 (Item 308) — `/admin/integrations`.
 *
 * Top-level gate: requer `integrations-manage` (manage mode) OU
 * `integrations-read` (read-only — DPO Regulated). Sem nenhuma, mostra
 * `FeatureLocked` apontando a capability necessária.
 *
 * Estrutura A4 do Briefing 5 V2 §9.2 preservada: 4 tabs preset desde dia 1.
 * - ERPs / CRMs (S1) — única funcional.
 * - Canais — saúde por canal, teste de conexão e credencial (F2, 19/Set/2026).
 * - IA (S2) — redireciona para `/admin/ai-config` existente.
 * - Calendário (S4) — placeholder "Em breve".
 *
 * Capability `integrations-read` (DPO Regulated) renderiza ErpsCrmsTab em
 * modo `read-only` — frontend esconde botões; backend nega POST/PATCH/DELETE
 * via guard granular (defense-in-depth canonical).
 */
export function IntegrationsPage(): JSX.Element {
  const canManage = useCapability('integrations-manage');
  const canRead = useCapability('integrations-read');
  const [activeTab, setActiveTab] = useState<TabId>('erps-crms');

  // Side-effect: subscribe em mcp.handshake-completed + integrations.feature-toggled.
  // Invalida React Query → card transita de pending-handshake → active/failed
  // sem refresh manual. Task 16 inclui invalidação cross-feature de customer-context.
  useMcpHandshakeListener();

  if (!canManage && !canRead) {
    return <FeatureLocked capability="integrations-manage" />;
  }

  const mode: 'manage' | 'read-only' = canManage ? 'manage' : 'read-only';

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border-subtle bg-surface px-8 py-5">
        <h1 className="text-xl font-semibold text-text-primary">Integrações Externas</h1>
        <p className="mt-0.5 text-sm text-text-secondary">
          Gerencie conectores externos (ERPs, canais, IA, calendário) do seu tenant.
        </p>
      </div>

      <div className="flex border-b border-border-subtle bg-surface px-8" role="tablist">
        <TabButton
          id="erps-crms"
          label="ERPs / CRMs"
          active={activeTab === 'erps-crms'}
          onClick={setActiveTab}
        />
        {/* F2 (19/Set/2026) — a aba saiu do «Em breve». Deixar o `disabled` aqui teria
            tornado a fatia inteira INALCANÇÁVEL: a tela existiria e ninguém chegaria
            nela. Achado na validação viva, não pelos testes. */}
        <TabButton
          id="channels"
          label="Canais"
          active={activeTab === 'channels'}
          onClick={setActiveTab}
        />
        <TabButton id="ai" label="IA" active={activeTab === 'ai'} onClick={setActiveTab} />
        <TabButton
          id="calendar"
          label="Calendário"
          badge="Em breve"
          disabled
          active={activeTab === 'calendar'}
          onClick={setActiveTab}
        />
      </div>

      <div className="flex-1 overflow-y-auto">
        {activeTab === 'erps-crms' && <ErpsCrmsTab mode={mode} />}
        {activeTab === 'channels' && <ChannelsTab mode={mode} />}
        {activeTab === 'ai' && <AiTab />}
        {activeTab === 'calendar' && <CalendarTab />}
      </div>
    </div>
  );
}

interface TabButtonProps {
  id: TabId;
  label: string;
  badge?: string;
  disabled?: boolean;
  active: boolean;
  onClick: (id: TabId) => void;
}

function TabButton({ id, label, badge, disabled, active, onClick }: TabButtonProps): JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      disabled={disabled}
      onClick={() => {
        if (!disabled) onClick(id);
      }}
      className={`px-4 py-3 text-sm font-medium ${
        active ? 'border-b-2 border-accent text-text-primary' : 'text-text-secondary'
      } ${disabled ? 'cursor-not-allowed opacity-60' : 'hover:text-text-primary'}`}
    >
      {label}
      {badge !== undefined && (
        <span className="ml-2 rounded bg-bg-muted px-2 py-0.5 text-xs">{badge}</span>
      )}
    </button>
  );
}
